import { EntityKind, type FinancialPlanData } from "../../generated/core/v1/financial_plan";
import { LoanType } from "../../generated/core/v1/debts";
import { type Family, PriorityKind } from "../../generated/core/v1/family";
import { PropertyKind } from "../../generated/core/v1/property";
import { ChequingAccount_Type } from "../../generated/core/v1/accounts";
import { groupRrspUuids } from "./groupRrsp";

/**
 * Names and labels for the uuids the plan refers to, read from the family document. The plan
 * carries uuids only; the family is where a user's own names live. Accounts the engine opens (a
 * TFSA, an RRSP, a mortgage for a bought home) aren't in the family, so the given plans' states
 * name them by kind and owner ("Alex’s TFSA", "Alex’s group RRSP").
 */
export type EntityInfo = {
  uuid: string;
  name: string;
  /** What kind of thing it is, in words ("TFSA", "Line of credit"). */
  kindLabel: string;
  kind?: EntityKind;
  memberUuid?: string;
  /** Annual interest rate, percent, as the user entered it (debts, cash). */
  rate?: number;
};

export type EntityIndex = {
  get(uuid: string | undefined): EntityInfo | undefined;
  name(uuid: string | undefined, fallback?: string): string;
  /** A person's or dependent's first name. */
  person(uuid: string | undefined): string;
  all: EntityInfo[];
};

export const KIND_LABEL: Record<EntityKind, string> = {
  [EntityKind.UNSPECIFIED]: "Account",
  [EntityKind.CHEQUING]: "Chequing",
  [EntityKind.SAVINGS]: "Savings",
  [EntityKind.RRSP]: "RRSP",
  [EntityKind.TFSA]: "TFSA",
  [EntityKind.FHSA]: "FHSA",
  [EntityKind.RESP]: "RESP",
  [EntityKind.RRIF]: "RRIF",
  [EntityKind.NON_REGISTERED]: "Non-registered",
  [EntityKind.DC_PENSION]: "Workplace pension account",
  [EntityKind.MORTGAGE]: "Mortgage",
  [EntityKind.LOAN]: "Loan",
  [EntityKind.CREDIT_CARD]: "Credit card",
  [EntityKind.PROPERTY]: "Home",
};

export const LOAN_LABEL: Record<LoanType, string> = {
  [LoanType.UNSPECIFIED]: "Loan",
  [LoanType.LINE_OF_CREDIT]: "Line of credit",
  [LoanType.CREDIT_CARD]: "Credit card",
  [LoanType.PERSONAL_LOAN]: "Personal loan",
  [LoanType.STUDENT_LOAN]: "Student loan",
  [LoanType.CAR_LOAN]: "Car loan",
  [LoanType.OTHER]: "Other debt",
  [LoanType.CRA_DEBT]: "Money owed to the CRA",
  [LoanType.FAMILY_LOAN]: "Loan from family",
  [LoanType.BUY_NOW_PAY_LATER]: "Buy now, pay later",
  [LoanType.BUSINESS_LOAN]: "Business loan",
};

/** An unnamed property's name, by kind, as the engine's defaults pass names it ("Cottage 2"). */
export const PROPERTY_KIND_NAME: Record<PropertyKind, string> = {
  [PropertyKind.UNSPECIFIED]: "Property",
  [PropertyKind.PRINCIPAL_RESIDENCE]: "Home",
  [PropertyKind.RENTAL]: "Rental property",
  [PropertyKind.COTTAGE]: "Cottage",
  [PropertyKind.SECONDARY_HOME]: "Secondary home",
  [PropertyKind.INVESTMENT]: "Investment property",
  [PropertyKind.LAND]: "Land",
  [PropertyKind.FAMILY_MEMBER_HOME]: "Family member’s home",
  [PropertyKind.OTHER]: "Property",
};

export const PRIORITY_KIND_LABEL: Record<PriorityKind, string> = {
  [PriorityKind.UNSPECIFIED]: "A priority",
  [PriorityKind.CHILD_EDUCATION]: "A child’s education",
  [PriorityKind.BUY_HOME]: "Buying a home",
  [PriorityKind.BIGGER_HOME]: "A bigger home",
  [PriorityKind.RENOVATION]: "Home renovation",
  [PriorityKind.TRAVEL]: "Travel every year",
  [PriorityKind.WEDDING]: "A wedding",
  [PriorityKind.START_BUSINESS]: "Starting a business",
  [PriorityKind.NEW_CAR]: "A new car",
  [PriorityKind.HELP_FAMILY]: "Helping family",
  [PriorityKind.GIVING]: "Giving to causes",
  [PriorityKind.CUSTOM]: "Something of your own",
};

export function buildEntityIndex(
  family: Family | undefined | null,
  ...plans: (FinancialPlanData | undefined | null)[]
): EntityIndex {
  const map = new Map<string, EntityInfo>();
  const add = (info: EntityInfo) => info.uuid && map.set(info.uuid, info);
  const named = (name: string, fallback: string) => (name && name.trim()) || fallback;
  const groups = groupRrspUuids(family, ...plans);
  const label = (uuid: string, kind: EntityKind) =>
    kind === EntityKind.RRSP && groups.has(uuid) ? "group RRSP" : KIND_LABEL[kind];

  if (family) {
    family.persons.forEach((p) => {
      add({ uuid: p.uuid, name: p.givenName || "You", kindLabel: "Person" });
      const owner = p.uuid;
      const who = p.givenName;
      const acct = (uuid: string, name: string, kind: EntityKind, rate?: number) =>
        add({
          uuid,
          name: named(name, who ? `${who}’s ${label(uuid, kind)}` : KIND_LABEL[kind]),
          kindLabel: KIND_LABEL[kind],
          kind,
          memberUuid: owner,
          rate,
        });
      p.chequingAccounts.forEach((a) =>
        acct(
          a.uuid,
          a.name,
          a.type === ChequingAccount_Type.SAVINGS ? EntityKind.SAVINGS : EntityKind.CHEQUING,
          a.interestApr,
        ),
      );
      p.rrspAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.RRSP));
      p.tfsaAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.TFSA));
      p.fhsaAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.FHSA));
      p.respAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.RESP));
      p.rrifAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.RRIF));
      p.tradingAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.NON_REGISTERED));
      p.dcPensionAccounts.forEach((a) => acct(a.uuid, a.name, EntityKind.DC_PENSION));
      p.creditCards.forEach((c) => acct(c.uuid, c.name, EntityKind.CREDIT_CARD, c.interest));
      p.jobs.forEach((j) =>
        add({
          uuid: j.uuid,
          name: named(j.title || j.employer, who ? `${who}’s job` : "Job"),
          kindLabel: "Job",
          memberUuid: owner,
        }),
      );
      p.pensions.forEach((pn) =>
        add({
          uuid: pn.uuid,
          name: named(pn.name, "Pension"),
          kindLabel: "Pension",
          memberUuid: owner,
        }),
      );
      p.untaxedIncomes.forEach((u) =>
        add({
          uuid: u.uuid,
          name: named(u.name, "Other income"),
          kindLabel: "Income",
          memberUuid: owner,
        }),
      );
    });
    family.dependents.forEach((d) =>
      add({ uuid: d.uuid, name: d.givenName || "Your child", kindLabel: "Child" }),
    );
    family.loans.forEach((l) =>
      add({
        uuid: l.uuid,
        name: named(l.name, LOAN_LABEL[l.type] ?? "Loan"),
        kindLabel: LOAN_LABEL[l.type] ?? "Loan",
        kind: l.type === LoanType.CREDIT_CARD ? EntityKind.CREDIT_CARD : EntityKind.LOAN,
        rate: l.interest,
      }),
    );
    family.mortgages.forEach((m) =>
      add({
        uuid: m.uuid,
        name: named(m.name, "Mortgage"),
        kindLabel: "Mortgage",
        kind: EntityKind.MORTGAGE,
        rate: m.interest,
      }),
    );
    const perKind = new Map<string, number>();
    family.properties.forEach((p) => {
      const label = PROPERTY_KIND_NAME[p.kind] ?? "Property";
      const n = (perKind.get(label) ?? 0) + 1;
      perKind.set(label, n);
      add({
        uuid: p.uuid,
        name: named(p.name, n === 1 ? label : `${label} ${n}`),
        kindLabel: "Property",
        kind: EntityKind.PROPERTY,
      });
    });
    family.financialPriorities.forEach((p) =>
      add({
        uuid: p.uuid,
        name: named(p.name, PRIORITY_KIND_LABEL[p.kind] ?? "A priority"),
        kindLabel: "Priority",
      }),
    );
    family.homebuyingPriorities.forEach((p) =>
      add({
        uuid: p.uuid,
        name: named(p.name, PRIORITY_KIND_LABEL[PriorityKind.BUY_HOME]),
        kindLabel: "Priority",
      }),
    );
    family.expenses.forEach((e) =>
      add({ uuid: e.uuid, name: named(e.name, "Expense"), kindLabel: "Expense" }),
    );
  }

  for (const plan of plans) {
    for (const state of plan?.monthlyStates ?? []) {
      for (const b of [...state.accounts, ...state.debts, ...state.assets]) {
        if (!b.entityUuid || map.has(b.entityUuid)) continue;
        const who = b.memberUuid ? map.get(b.memberUuid)?.name : undefined;
        add({
          uuid: b.entityUuid,
          name: who ? `${who}’s ${label(b.entityUuid, b.kind)}` : KIND_LABEL[b.kind],
          kindLabel: KIND_LABEL[b.kind],
          kind: b.kind,
          memberUuid: b.memberUuid || undefined,
        });
      }
    }
  }

  const all = [...map.values()];
  return {
    all,
    get: (uuid) => (uuid ? map.get(uuid) : undefined),
    name: (uuid, fallback = "An account") => (uuid && map.get(uuid)?.name) || fallback,
    person: (uuid) => (uuid && map.get(uuid)?.name) || "",
  };
}

/** "Mortgage, 4.79%". */
export function nameWithRate(info: EntityInfo | undefined, fallback: string): string {
  if (!info) return fallback;
  return info.rate !== undefined ? `${info.name}, ${+info.rate.toFixed(2)}%` : info.name;
}
